import { useEffect, useState } from 'react';
import { fetchCollection } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import LogoIconDecoration from '../components/LogoIconDecoration';

export default function Contact() {
  const [pageContent, setPageContent] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    company_name: 'Site Facility ApS',
    address: 'Vestkraftgade 1, 2nd Floor, 6700 Esbjerg, Denmark',
    phone: '+45 21 79 57 53',
    email: 'jh@sitefacility.dk',
    contact_person: 'Jakob Krøjgaard Heskjær',
    cvr: '40030964',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Fetch contact page content
        const pages = await fetchCollection('pages');
        const contactPage = pages.find(p => p.slug.en === 'contact');
        if (contactPage) {
          setPageContent(contactPage);
        }

        // Fetch contact information
        const contactDetails = await fetchCollection('contact_information');
        if (contactDetails && contactDetails.length > 0) {
          setContactInfo(contactDetails[0]);
        }
      } catch (error) {
        console.error('Error loading contact page data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Update document metadata
  useEffect(() => {
    if (pageContent) {
      document.title = pageContent.meta_title || 'Contact – Site Facility ApS';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageContent.meta_description || '');
      }
    } else {
      document.title = 'Contact – Site Facility ApS';
    }
  }, [pageContent]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner-third animate-spin text-4xl text-accent" />
          <p className="text-slate-500 font-medium">Loading contact details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[73px] bg-white min-h-screen">
      {/* Header Section */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0758-scaled.jpg"
            alt="Contact Background"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center space-y-4">
          <span className="text-accent text-xs font-bold tracking-widest uppercase">Get In Touch</span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
            {pageContent?.title || 'Contact Us'}
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We are ready to provide personalized support and ensure your accommodation requirements are met effectively and efficiently.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 relative overflow-hidden">
        <LogoIconDecoration className="w-[550px] h-[550px] opacity-[0.015] -right-24 top-12 rotate-12" />
        <LogoIconDecoration className="w-[450px] h-[450px] opacity-[0.015] -left-20 bottom-12 -rotate-12" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
          {/* Left Column: Contact Info & Text */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-slate-50 p-8 md:p-10 rounded-2xl border border-slate-100/80 space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-primary font-display">Contact Information</h2>
              
              {pageContent?.content && (
                <div 
                  className="prose prose-slate max-w-none text-slate-600 leading-relaxed text-sm space-y-4"
                  dangerouslySetInnerHTML={{ __html: pageContent.content }}
                />
              )}

              <div className="pt-6 border-t border-slate-100 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-building text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Company</span>
                    <span className="text-sm font-semibold text-primary">{contactInfo.company_name}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-location-dot text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Address</span>
                    <span className="text-sm font-semibold text-primary leading-relaxed">{contactInfo.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-user-tie text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Contact Person</span>
                    <span className="text-sm font-semibold text-primary">{contactInfo.contact_person}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-phone text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Phone</span>
                    <div className="flex flex-col gap-1">
                      {contactInfo.phone.split('/').map((p, idx) => (
                        <a 
                          key={idx}
                          href={`tel:${p.trim().replace(/\s+/g, '')}`} 
                          className="text-sm font-semibold text-primary hover:text-accent transition-colors duration-200 whitespace-nowrap"
                        >
                          {p.trim()}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-envelope text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Email</span>
                    <a 
                      href={`mailto:${contactInfo.email}`} 
                      className="text-sm font-semibold text-primary hover:text-accent transition-colors duration-200 whitespace-nowrap"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-accent">
                    <i className="fa-regular fa-id-card text-lg" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">CVR Number</span>
                    <span className="text-sm font-semibold text-primary">{contactInfo.cvr}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
